import React from 'react';
import styled from 'styled-components';
import { Translation } from '~/components/Translation';
import { ImageBlock, ImageBlockBgColors } from '~/components/Marketing';
import RecordingsCapture from '~/components/RecordingsCapture';
import type { LearnMoreBlock as LearnMoreBlockType } from '@sy/ssr-types';
import { featureImages, type FeatureType } from '../../assets/imageImports';

const LearnMoreImage = styled.img`
	display: block;
	height: auto;
	width: 100%;
	max-width: 640px;
`;

const TitleHighlight = styled.span`
	color: ${({ theme }) =>
		theme.tokens.COLOR_TOKENS_TEXT_INVERTIBLE_PRIMARY_PRIMARY};
`;

const componentMap: Record<string, React.ReactNode> = {
	recordingsCapture: (
		<RecordingsCapture customWidth="100%" customMaxWidth="640px" />
	),
};

interface LearnMoreBlockProps {
	feature: FeatureType;
	blocks: LearnMoreBlockType[];
}

const LearnMoreBlock: React.FC<LearnMoreBlockProps> = ({ feature, blocks }) => {
	const images = featureImages[feature];
	if (!images) {
		return null;
	}

	const learnMoreImages = images.learnMoreBlocksImages;
	if (!learnMoreImages) {
		return null;
	}

	return (
		<>
			{blocks.map((block, index) => {
				// Alternate color and reverse for each block
				const color =
					index % 2 === 0 ? ImageBlockBgColors.Slate : ImageBlockBgColors.White;
				const reverse = index % 2 === 0;

				let asset: React.ReactNode = null;
				if (block.assetType === 'component') {
					asset = componentMap[block.assetName];
				} else if (block.assetType === 'image') {
					const imageSrc =
						learnMoreImages[block.assetName as keyof typeof learnMoreImages];
					if (imageSrc) {
						asset = <LearnMoreImage src={imageSrc} alt="" />;
					}
				}

				if (!asset) {
					return null;
				}

				return (
					<ImageBlock
						key={index}
						color={color}
						reverse={reverse}
						image={asset}
						wrapPadding="80px"
						header={
							block.titleKey.highlightPart === 'part1' ? (
								<>
									<TitleHighlight>
										<Translation tid={block.titleKey.part1} />
									</TitleHighlight>
									<Translation tid={block.titleKey.part2} />
								</>
							) : (
								<>
									<Translation tid={block.titleKey.part1} />
									<TitleHighlight>
										<Translation tid={block.titleKey.part2} />
									</TitleHighlight>
								</>
							)
						}
						subHeader={<Translation tid={block.descriptionKey} />}
					/>
				);
			})}
		</>
	);
};

export default LearnMoreBlock;
